// Global Instructions Rule Applied!

import { TableFilterOptionsResponse } from '../../../state_manager/rpc_models/get_table_filter_options_response.ts'
import {
  DealsPaginatedResponse,
  fetchDealsFilterOptions,
  fetchDealsPaginated,
  fetchDealsFiltered
} from '../services/dealsService.ts'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useSelector } from 'react-redux'
import {
  setDeals,
  setFilterOptions,
  setActiveFilters,
  setActiveSorting,
  clearActiveFilters,
} from '../../../state_manager/state_update_actions/deals/index.ts'
import { DealTarget, FilterValue, SortConfig } from '../../../state_manager/store_models/deals.ts'
import type { AppDispatch } from '../../../state_manager/store.ts'
import {
  mergeUserPreferenceRowData,
  removeUserPreferenceForMenuItem
} from '../../../state_manager/state_update_actions/global_configs/actions.ts'
import { getAccessToken } from '../../../core/auth/authService'
import { store } from '../../../state_manager/store.ts'
import { fetchGlobalConfigsThunk } from '../../global_configs/controller.ts'
import { useMenuItemUserPreference } from '../../global_configs/useMenuItemUserPreference.ts'
import {
  areFilterValuesEqual,
  DEFAULT_DEALS_TABLE_FILTERS,
  areSortConfigsEqual,
  PREF_TABLE_COLUMN_KEYS,
  PREF_TABLE_FILTERS,
  PREF_TABLE_SORTING,
  resolveTableFiltersFromPreferenceRow,
  resolveTableSortingFromPreferenceRow
} from '../../global_configs/tableViewPreferences.ts'
import { useReportingDefaultSorting } from '../../global_configs/useReportingDefaultSorting.ts'
import { MENU_ITEM_KEY_DEALS } from '../../../state_manager/globals.ts'
import { useDealsReportId } from '../../../state_manager/hooks/useDealsReportId.ts'

export const useDealsData = (filterOptionKeys?: string[]) => {
  const dispatch = useDispatch<AppDispatch>()

  const { menuItemId, preferenceRow, persistPreference, deletePreference } =
    useMenuItemUserPreference(MENU_ITEM_KEY_DEALS)

  const dealsReportId = useDealsReportId()
  const defaultTableSorting = useReportingDefaultSorting(dealsReportId)

  const [dealsDataLoading, setDealsDataLoading] = useState(false)
  const [dealsFilterOptionsLoading, setDealsFilterOptionsLoading] = useState(true)
  const [prefsReady, setPrefsReady] = useState(false)
  const skipPersistRef = useRef(true)

  const dealsData = useSelector((state: any) => state.deals.deal.data)
  const dealsFilterOptions = useSelector((state: any) => state.deals.deal.filterOptions)
  const activeFilters = useSelector((state: any) => state.deals.deal.activeFilters)
  const activeSorting = useSelector((state: any) => state.deals.deal.activeSorting)

  const menuItems = useSelector((state: any) => state.globalConfigs?.menu_items ?? [])

  useEffect(() => {
    if (menuItems.length === 0) return
    if (!menuItemId) {
      dispatch(setActiveFilters({ filters: DEFAULT_DEALS_TABLE_FILTERS, target: DealTarget.DEAL }))
      dispatch(setActiveSorting({ sorting: defaultTableSorting, target: DealTarget.DEAL }))
      setPrefsReady(true)
      return
    }
    const filters = resolveTableFiltersFromPreferenceRow(preferenceRow, DEFAULT_DEALS_TABLE_FILTERS)
    const sorting = resolveTableSortingFromPreferenceRow(preferenceRow, defaultTableSorting)
    if (!areFilterValuesEqual(filters, activeFilters)) {
      dispatch(setActiveFilters({ filters, target: DealTarget.DEAL }))
    }
    if (!areSortConfigsEqual(sorting, activeSorting)) {
      dispatch(setActiveSorting({ sorting, target: DealTarget.DEAL }))
    }
    setPrefsReady(true)
  }, [menuItemId, menuItems.length, preferenceRow, defaultTableSorting, dispatch])

  const handleFetchFilterOptions = useCallback(async () => {
    try {
      setDealsFilterOptionsLoading(true)
      const currentToken = getAccessToken(store.getState().auth)
      const keys =
        filterOptionKeys && filterOptionKeys.length > 0 ? filterOptionKeys : undefined
      const response: TableFilterOptionsResponse = await fetchDealsFilterOptions(currentToken, keys)
      dispatch(setFilterOptions({ response, target: DealTarget.DEAL }))
    } catch (e) {
      console.error('Error fetching filter options:', e)
    } finally {
      setDealsFilterOptionsLoading(false)
    }
  }, [dispatch, filterOptionKeys?.join('|') ?? ''])

  const handleFetchDeals = useCallback(
    async (sorting: SortConfig[] = [], filters?: FilterValue[]) => {
      try {
        setDealsDataLoading(true)
        const currentToken = getAccessToken(store.getState().auth)
        const hasFilters = filters && filters.length > 0
        const hasSorting = sorting.length > 0

        const response: DealsPaginatedResponse =
          hasFilters || hasSorting
            ? await fetchDealsFiltered(sorting, filters || [], 1, null, currentToken, dealsReportId)
            : await fetchDealsPaginated(1, null, currentToken, dealsReportId)

        dispatch(setDeals({ response, target: DealTarget.DEAL }))
      } catch (e) {
        console.error('Error fetching deals:', e)
      } finally {
        setDealsDataLoading(false)
      }
    },
    [dispatch, dealsReportId]
  )

  const handleSetFilters = useCallback(
    (filters: FilterValue[]) => {
      if (areFilterValuesEqual(filters, activeFilters)) return
      dispatch(setActiveFilters({ filters, target: DealTarget.DEAL }))
    },
    [dispatch, activeFilters]
  )

  const handleSetSorting = useCallback(
    (sorting: SortConfig[]) => {
      if (areSortConfigsEqual(sorting, activeSorting)) return
      dispatch(setActiveSorting({ sorting, target: DealTarget.DEAL }))
    },
    [dispatch, activeSorting]
  )

  const handleClearFilters = useCallback(() => {
    dispatch(clearActiveFilters({ target: DealTarget.DEAL }))
  }, [dispatch])

  const handleResetFilters = useCallback(() => {
    if (!menuItemId) {
      dispatch(setActiveFilters({ filters: DEFAULT_DEALS_TABLE_FILTERS, target: DealTarget.DEAL }))
      return
    }
    void (async () => {
      const result = await deletePreference()
      if (!result.success) {
        if (process.env.NODE_ENV === 'development') {
          console.error('Failed to delete deals table preferences:', result.error)
        }
        return
      }
      dispatch(removeUserPreferenceForMenuItem(menuItemId))
      skipPersistRef.current = true
      dispatch(setActiveFilters({ filters: DEFAULT_DEALS_TABLE_FILTERS, target: DealTarget.DEAL }))
      dispatch(setActiveSorting({ sorting: defaultTableSorting, target: DealTarget.DEAL }))
    })()
  }, [dispatch, menuItemId, deletePreference, defaultTableSorting])

  const handleClearSorting = useCallback(() => {
    dispatch(setActiveSorting({ sorting: defaultTableSorting, target: DealTarget.DEAL }))
  }, [dispatch, defaultTableSorting])

  /** Merges with latest Redux preference row + current filters/sort; preserves `table_column_keys` etc. */
  const persistMergedPreference = useCallback(
    async (extra: Record<string, unknown> = {}) => {
      if (!menuItemId) return { success: false as const, error: 'No menu item' }
      const reports = store.getState().globalConfigs?.user_preferences_reports ?? []
      const hadExistingRow = reports.some((p) => p.menu_item_id === menuItemId)
      const row = reports.find((p) => p.menu_item_id === menuItemId)
      const merged: Record<string, unknown> = {
        ...(row?.data ?? {}),
        ...extra,
        [PREF_TABLE_FILTERS]: activeFilters,
        [PREF_TABLE_SORTING]: activeSorting
      }
      const result = await persistPreference(undefined, merged)
      if (result.success) {
        if (hadExistingRow) {
          dispatch(mergeUserPreferenceRowData(menuItemId, merged))
        } else {
          const user = store.getState().auth.user
          if (user) {
            await fetchGlobalConfigsThunk(user)(dispatch)
          }
        }
      }
      return result
    },
    [menuItemId, activeFilters, activeSorting, persistPreference, dispatch]
  )

  const persistColumnFieldOrder = useCallback(
    async (fieldNames: string[]) => {
      return persistMergedPreference({ [PREF_TABLE_COLUMN_KEYS]: fieldNames })
    },
    [persistMergedPreference]
  )

  const resetColumnLayoutToDefault = useCallback(async () => {
    if (!menuItemId) return { success: false as const, error: 'No menu item' }
    const reports = store.getState().globalConfigs?.user_preferences_reports ?? []
    const hadExistingRow = reports.some((p) => p.menu_item_id === menuItemId)
    const row = reports.find((p) => p.menu_item_id === menuItemId)
    const base: Record<string, unknown> = { ...(row?.data ?? {}) }
    delete base[PREF_TABLE_COLUMN_KEYS]
    const merged: Record<string, unknown> = {
      ...base,
      [PREF_TABLE_FILTERS]: activeFilters,
      [PREF_TABLE_SORTING]: activeSorting
    }
    const result = await persistPreference(undefined, merged)
    if (result.success) {
      if (hadExistingRow) {
        dispatch(mergeUserPreferenceRowData(menuItemId, merged))
      } else {
        const user = store.getState().auth.user
        if (user) {
          await fetchGlobalConfigsThunk(user)(dispatch)
        }
      }
    }
    return result
  }, [menuItemId, activeFilters, activeSorting, persistPreference, dispatch])

  useEffect(() => {
    if (!prefsReady) return
    const currentToken = getAccessToken(store.getState().auth)
    if (currentToken) {
      handleFetchFilterOptions()
      handleFetchDeals(activeSorting, activeFilters)
    } else {
      setDealsFilterOptionsLoading(false)
    }
  }, [prefsReady, activeFilters, activeSorting, handleFetchFilterOptions, handleFetchDeals])

  useEffect(() => {
    if (!prefsReady || !menuItemId) return
    if (skipPersistRef.current) {
      skipPersistRef.current = false
      return
    }
    void persistMergedPreference().catch((err) => {
      if (process.env.NODE_ENV === 'development') {
        console.error('Failed to persist deals table preferences:', err)
      }
    })
  }, [prefsReady, menuItemId, activeFilters, activeSorting, persistMergedPreference])

  return {
    dealsFilterOptions: dealsFilterOptions.data,
    dealsData,
    dealsDataLoading,
    dealsFilterOptionsLoading,
    activeFilters,
    activeSorting,
    preferenceRow,
    handleFetchDeals,
    handleFetchFilterOptions,
    handleSetFilters,
    handleSetSorting,
    handleClearFilters,
    handleClearSorting,
    defaultTableFilters: DEFAULT_DEALS_TABLE_FILTERS,
    defaultTableSorting,
    handleResetFilters,
    persistColumnFieldOrder,
    resetColumnLayoutToDefault
  }
}
